import * as T from "three";
import { SpriteTrail } from "./sprite-trail.js";
const framePools = new WeakMap();
// The SDK CPU GammaToLinear constants use a quantized pow(2.2) table;
// sprite_vs20 uses pow(2.2) for vertex RGB and preserves vertex alpha.
export function spriteGamma(value) { return value >= .95 ? 1 : Math.pow(Math.round(Math.max(0,value)*255)/255,2.2); }

function acquireFrames(source, s3tc, repeat) {
    let pools = framePools.get(source);
    if (!pools) { pools = new Map(); framePools.set(source, pools); }
    const key = `${!!s3tc}:${repeat}`;
    let pool = pools.get(key);
    if (!pool) {
        const frames = textures(source.texture, s3tc);
        for (const texture of frames) {
            if (Number(source.material?.values.$nosrgb) === 1) texture.colorSpace = T.NoColorSpace;
            texture.wrapS = repeat ? T.RepeatWrapping : T.ClampToEdgeWrapping;
        }
        pool = { frames, users: 0 }; pools.set(key, pool);
    }
    pool.users++;
    const lease = new T.EventDispatcher(); let disposed = false;
    lease.dispose = () => {
        if (disposed) return; disposed = true;
        if (--pool.users === 0) { pools.delete(key); for (const texture of pool.frames) texture.dispose(); }
        lease.dispatchEvent({ type: "dispose" });
    };
    return { frames: pool.frames, lease };
}

function textures(source, s3tc) {
    return (source.frameFaces ?? [source.faces]).map(faces => {
        const mipmaps = faces[0], first = mipmaps[0];
        const texture = source.compressed && s3tc
            ? new T.CompressedTexture(mipmaps, first.width, first.height,
                [13, 20].includes(source.format) ? T.RGBA_S3TC_DXT1_Format : source.format === 14 ? T.RGBA_S3TC_DXT3_Format : T.RGBA_S3TC_DXT5_Format)
            : new T.DataTexture(first.data, first.width, first.height, T.RGBAFormat,
                first.data instanceof Float32Array ? T.FloatType : T.UnsignedByteType);
        if (!source.compressed) texture.mipmaps = mipmaps;
        texture.colorSpace = T.SRGBColorSpace; texture.wrapS = texture.wrapT = T.ClampToEdgeWrapping;
        texture.minFilter = mipmaps.length > 1 ? T.LinearMipmapLinearFilter : T.LinearFilter;
        texture.magFilter = T.LinearFilter; texture.generateMipmaps = false; texture.needsUpdate = true;
        return texture;
    });
}

/** Source GetSpriteAxes, expressed in the viewer's Source XYZ coordinates. */
export function spriteAxes(orientation, matrix, camera, center, roll = 0) {
    camera.updateWorldMatrix(true, false);
    const right = new T.Vector3().setFromMatrixColumn(camera.matrixWorld, 0),
        up = new T.Vector3().setFromMatrixColumn(camera.matrixWorld, 1),
        forward = camera.getWorldDirection(new T.Vector3());
    if (orientation === 3) return { right: right.setFromMatrixColumn(matrix, 1).normalize().negate(), up: up.setFromMatrixColumn(matrix, 2).normalize() };
    if (orientation < 2) {
        if (orientation === 1) forward.copy(center).sub(camera.getWorldPosition(new T.Vector3())).normalize();
        if (Math.abs(forward.z) > .999848) return null;
        return { right: right.set(forward.y, -forward.x, 0).normalize(), up: up.set(0, 0, 1) };
    }
    if (orientation === 4 || roll) {
        const angle = roll * Math.PI / 180, r = right.clone();
        right.multiplyScalar(Math.cos(angle)).addScaledVector(up, Math.sin(angle));
        up.multiplyScalar(Math.cos(angle)).addScaledVector(r, -Math.sin(angle));
    }
    return { right, up };
}

// Native CSprite wraps only after maxFrame, which is frameCount - 1. A
// one-shot sprite turns off past that endpoint; frozen anchors ignore speed.
export function spriteFrameAt(state, time, sampleTime, frameCount) {
    const anchored = !!(state.flags & 4), active = anchored ? !!(state.frameFlags & 1) : true;
    const elapsed = anchored ? Math.max(0, time - state.frameTime) : time - sampleTime;
    const frame = (state.frame ?? 0) + elapsed / 1000 * (active ? state.framerate ?? 0 : 0);
    if (!anchored) return { frame: ((frame % frameCount) + frameCount) % frameCount, finished: false };
    if (frame > state.maxFrame) {
        if (state.frameFlags & 2) return { frame: state.maxFrame, finished: true };
        if (state.maxFrame > 0) return { frame: frame % state.maxFrame, finished: false };
    }
    return { frame, finished: false };
}

export class LiveSprite {
    constructor(source, s3tc, resources) {
        this.source = source; this.s3tc = s3tc; this.resources = resources; this.setFrames(false);
        const values = source.material?.values ?? {}, tint = String(values.$color ?? "[1 1 1]").match(/[-+]?(?:\d*\.)?\d+(?:e[-+]?\d+)?/gi)?.map(Number);
        this.noSRGB = Number(values.$nosrgb) === 1;
        this.tint = new T.Color(1,1,1);
        if (tint?.length >= 3 && tint.every(Number.isFinite)) {
            const components = tint.slice(0,3).map(value => String(values.$color).startsWith("{") ? value / 255 : value);
            this.tint.setRGB(...components.map(value => this.noSRGB || value>1 ? value : spriteGamma(value)));
        }
        this.alpha = Number.isFinite(Number(values.$alpha)) ? T.MathUtils.clamp(Number(values.$alpha),0,1) : 1;
        this.ignoreVertexColors = Number(values.$ignorevertexcolors) === 1;
        this.lampBeam = !!values.proxies?.lampbeam;
        // RGB lamp cards have no alpha channel. Black is the neutral background
        // for their light, rather than an opaque surface in a translucent draw.
        this.materialAdditive = Number(values.$additive) === 1 || this.lampBeam && [2, 3].includes(source.texture.format);
        const first = source.texture.faces[0][0]; this.width = first.width; this.height = first.height;
        this.geometry = new T.BufferGeometry();
        this.quadGeometry();
        // A sprite/trail is a flat card, so separate back/front transparency
        // passes only duplicate submission and shader-side switching.
        this.material = new T.MeshBasicMaterial({ map: this.frames[0], side: T.DoubleSide, forceSinglePass: true, toneMapped: false,
            alphaTest: Number(values.$alphatest) === 1 ? Number(values.$alphatestreference ?? 0.5) : 0 });
        this.blend = { value: 0 }; this.nextMaterial = this.material.clone();
        resources.add(this.geometry); resources.add(this.material); resources.add(this.nextMaterial);
        this.mesh = new T.Mesh(this.geometry, this.material); this.mesh.frustumCulled = false;
        this.nextMesh = new T.Mesh(this.geometry, this.nextMaterial); this.nextMesh.frustumCulled = false; this.nextMesh.visible = false;
        this.inverse = new T.Matrix4(); this.center = new T.Vector3(); this.point = new T.Vector3();
    }
    setFrames(repeat) {
        if (this.repeat === repeat && this.frameLease) return;
        if (this.frameLease) { this.resources.delete(this.frameLease); this.frameLease.dispose(); }
        const { frames, lease } = acquireFrames(this.source, this.s3tc, repeat);
        this.frames = frames; this.frameLease = lease; this.repeat = repeat; this.resources.add(lease);
        if (this.material) this.material.map = frames[0];
        if (this.nextMaterial) this.nextMaterial.map = frames[0];
    }
    quadGeometry() {
        this.geometry.setAttribute("position", new T.BufferAttribute(new Float32Array(12), 3));
        this.geometry.setAttribute("uv", new T.BufferAttribute(new Float32Array([0, 1, 0, 0, 1, 0, 1, 1]), 2));
        this.geometry.deleteAttribute("color"); this.geometry.setIndex([0, 2, 1, 0, 3, 2]); this.geometry.setDrawRange(0, 6);
    }
    transition(field, target, duration, sampleTime, time) {
        let value = this[field];
        if (!value) value = this[field] = { target, from: target, start: sampleTime };
        if (value.target !== target) { value.from = value.target; value.target = target; value.start = sampleTime; }
        const fraction = duration > 0 ? T.MathUtils.clamp((time - value.start) / (duration * 1000), 0, 1) : 1;
        return T.MathUtils.lerp(value.from, value.target, fraction);
    }
    update(entity, state, appearance, time, view, entities) {
        const camera = view.camera; if (!camera) return;
        this.setFrames(!!state.trail);
        // Quad bounds are refreshed below after attachment, scale and camera
        // orientation changes. Trails retain historical vertices and need
        // their existing uncullable path until they have active-range bounds.
        this.mesh.frustumCulled = this.nextMesh.frustumCulled = !state.trail;
        if (!state.trail && this.trail) {
            this.geometry.dispose(); this.quadGeometry(); this.trail = null; this.mode = undefined;
            this.material.vertexColors = false; this.material.needsUpdate = true;
            this.nextMaterial.vertexColors = false; this.nextMaterial.needsUpdate = true;
        }
        const sampleTime = entity.state.sampleTime ?? time;
        entity.object.updateWorldMatrix(true, false);
        this.center.setFromMatrixPosition(entity.object.matrixWorld);
        const attached = state.attachedIndex && entities?.get(`${state.attachedIndex}:${state.attachedGeneration}`),
            attachment = attached && (state.attachment ? attached.rig?.attachments[state.attachment - 1] : attached.object);
        if (attachment) { attachment.updateWorldMatrix(true, false); this.center.setFromMatrixPosition(attachment.matrixWorld); }
        let spriteCamera = camera;
        if (entity.inSky && view.skyDefinition) {
            spriteCamera = camera.clone(); spriteCamera.position.copy(camera.position).divideScalar(view.skyDefinition.scale).add(view.skyDefinition.origin);
        }
        const axes = spriteAxes(state.trail ? 2 : this.source.orientation, entity.object.matrixWorld, spriteCamera, this.center,
            state.trail ? 0 : entity.state.components[1]?.angles[2] ?? 0);
        const mode = appearance.renderMode ?? 0;
        const predictedFrame = spriteFrameAt(state, time, sampleTime, this.frames.length);
        this.mesh.visible = !!axes && mode !== 6 && mode !== 10 && !predictedFrame.finished;
        this.nextMesh.visible = this.mesh.visible && mode === 7;
        if (!this.mesh.visible) return;
        let scale = this.transition("scale", state.scale ?? 1, state.scaleTime ?? 0, sampleTime, time);
        if (state.flags & 1) scale /= Math.min(this.width, this.height);
        scale = scale > 0 ? scale : 1;
        scale *= entity.object.getWorldScale(new T.Vector3()).x;
        let brightness = this.transition("brightness", state.brightness ?? (appearance.color >>> 24), state.brightnessTime ?? 0, sampleTime, time) / 255;
        if (this.lampBeam) {
            const eye = spriteCamera.getWorldPosition(new T.Vector3()), direction = this.center.clone().sub(eye).normalize();
            brightness *= 1 - Math.abs(direction.z);
        }
        const glow = mode === 3 || mode === 9;
        if (glow) {
            const eye = spriteCamera.getWorldPosition(new T.Vector3()), distance = eye.distanceTo(this.center);
            if (state.renderFX !== 14) {
                brightness *= Math.min(1, 1200 ** 2 / Math.max(1, distance ** 2));
                if (mode === 3) scale *= distance / 200;
            }
            if (view.visibility?.trace) {
                const radius = state.glowProxySize ?? 2;
                const cached = this.glowSample;
                // BSP occlusion is static. Reuse it only for identical rays;
                // camera, attachment, orientation and proxy-size changes invalidate.
                if (!cached || cached.visibility !== view.visibility || cached.radius !== radius ||
                    !cached.eye.equals(eye) || !cached.center.equals(this.center) ||
                    !cached.right.equals(axes.right) || !cached.up.equals(axes.up)) {
                    const sample = this.glowSample ??= { eye: new T.Vector3(), center: new T.Vector3(), right: new T.Vector3(), up: new T.Vector3() };
                    sample.visibility = view.visibility; sample.radius = radius;
                    sample.eye.copy(eye); sample.center.copy(this.center); sample.right.copy(axes.right); sample.up.copy(axes.up);
                    let visible = 0;
                    for (const [x, y] of [[0,0],[-1,0],[1,0],[0,-1],[0,1]]) {
                        this.point.copy(this.center).addScaledVector(axes.right, x * radius).addScaledVector(axes.up, y * radius);
                        if ((view.visibility.trace(eye.toArray(), this.point.toArray())?.fraction ?? 1) >= .999) visible++;
                    }
                    sample.fraction = visible / 5;
                }
                brightness *= this.glowSample.fraction;
            }
        }
        const wrapped = predictedFrame.frame, index = Math.min(Math.floor(wrapped), this.frames.length - 1);
        this.material.map = this.frames[index]; this.nextMaterial.map = this.frames[(index + 1) % this.frames.length];
        this.blend.value = mode === 7 ? T.MathUtils.clamp(wrapped - index, 0, 1) : 0;
        this.material.color.setRGB(mode ? (appearance.color & 255) / 255 : 1,
            mode ? (appearance.color >>> 8 & 255) / 255 : 1, mode ? (appearance.color >>> 16 & 255) / 255 : 1,
            T.LinearSRGBColorSpace);
        if (!this.noSRGB) for (const key of ["r","g","b"]) this.material.color[key] **= 2.2;
        if ((mode === 5 || mode === 7) && this.ignoreVertexColors) { this.material.color.setRGB(1,1,1); brightness = 1; }
        if (mode === 5) {
            this.material.color.multiply(this.tint); brightness *= this.alpha;
        }
        const hdr = state.hdrScale ?? 1;
        this.material.color.multiplyScalar(this.noSRGB || hdr>1 ? hdr : spriteGamma(hdr));
        this.material.opacity = mode ? brightness : 1;
        if (mode === 7) {
            // Two independent additive frame draws preserve each VTF frame's
            // alpha. Source replaces authored $color with grey $alpha weights.
            this.nextMaterial.color.copy(this.material.color);
            const first = this.alpha * (1-this.blend.value), second = this.alpha * this.blend.value;
            this.material.color.multiplyScalar(this.noSRGB ? first : spriteGamma(first));
            this.nextMaterial.color.multiplyScalar(this.noSRGB ? second : spriteGamma(second));
            this.nextMaterial.opacity = this.material.opacity;
        }
        const additive = glow || mode === 5 || mode === 7 || this.materialAdditive;
        if (this.mode !== mode) {
            this.mode = mode; this.material.transparent = true; this.material.depthWrite = false;
            this.material.depthTest = !glow;
            this.material.blending = mode === 8 ? T.CustomBlending : additive ? T.AdditiveBlending : T.NormalBlending;
            // Source's AlphaAdd passes are src*a+dst*(1-a), then
            // src*(1-a)+previous. Their sum is src+dst*(1-a), including alpha.
            if (mode === 8) { this.material.blendSrc = T.OneFactor; this.material.blendDst = T.OneMinusSrcAlphaFactor; }
            this.material.needsUpdate = true;
            this.nextMaterial.transparent = true; this.nextMaterial.depthWrite = false; this.nextMaterial.depthTest = true;
            this.nextMaterial.blending = T.AdditiveBlending; this.nextMaterial.needsUpdate = true;
        }
        if (state.trail) {
            if (!this.trail) {
                this.geometry.dispose();
                this.trail = new SpriteTrail(this.geometry, this.material);
                this.nextMaterial.vertexColors = true; this.nextMaterial.needsUpdate = true;
            }
            this.material.transparent = true; this.material.depthWrite = false;
            this.inverse.copy(entity.object.matrixWorld).invert();
            this.trail.update(this.center, spriteCamera, this.inverse, state.trail, time);
            return;
        }
        const [ox, oy] = this.source.origin, positions = this.geometry.attributes.position;
        this.inverse.copy(entity.object.matrixWorld).invert();
        for (const [i, x, y] of [[0, -ox, oy - 1], [1, -ox, oy], [2, 1 - ox, oy], [3, 1 - ox, oy - 1]]) {
            this.point.copy(this.center).addScaledVector(axes.right, x * this.width * scale).addScaledVector(axes.up, y * this.height * scale).applyMatrix4(this.inverse);
            positions.setXYZ(i, this.point.x, this.point.y, this.point.z);
        }
        positions.needsUpdate = true;
        this.geometry.computeBoundingSphere();
    }
}
